import {
  scaleBand,
  scaleLinear,
  type ScaleBand,
  type ScaleLinear,
} from 'd3-scale';
import { max } from 'd3-array';
import { d3, wordWrap, shouldUpdateText } from './d3';
import { rangeBandOptions, LARGE_FONT_SIZE } from './utils';

const Y_AXIS_PADDING = 15;
const ANIMATION_DURATION = 1000;
const ANIMATION_DELAY = ANIMATION_DURATION / 4;
const BAR_TEXT_PADDING = 10;
const BAR_MIN_WIDTH = 3;
const FONT_SIZE_MULTIPLIER = 1.1;
const MARGIN_RIGHT_MAX_MULTIPLIER = 0.4;
const MARGIN_LEFT_MULTIPLIER = 0.1;

export interface RankingResultT {
  id: number;
  label: string;
  score: number;
}

export interface Data extends RankingResultT {
  index: number;
}

interface T {
  selector: string;
  data: Data[];
  fillColors: string[];
  textColor: string;
  animationMultiplier: 1 | 0;
  translate: (key: any) => string; // TODO: fix type
  width: number;
  height: number;
  isScoreHidden: boolean;
}

interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Ranking: animated visualization that sorts items by popularity
 */
export class Ranking implements T {
  readonly selector: string;
  readonly fillColors: string[];
  readonly textColor: string;
  readonly animationMultiplier: 1 | 0;
  readonly translate: (key: string) => string;
  readonly x: ScaleLinear<number, number> = scaleLinear();
  readonly y: ScaleBand<number> = scaleBand();
  readonly width: number;
  readonly height: number;
  readonly isScoreHidden: boolean = false;

  data: Data[];
  margin: Margin = { top: 0, right: 0, bottom: 0, left: 0 };

  /**
   * @param selector: string selection to a dom node, e.g., #ranking-${questionId}
   * @param data: result data that can be ranked by score
   * @param fillColors: array of colors for bars, e.g., ['green', 'yellow', 'blue']
   * @param textColor: color string, e.g., #e8e8e8
   * @param animationMultiplier: value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   * @param translate: language translation function
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.data = props.data;
    this.fillColors = props.fillColors;
    this.textColor = props.textColor;
    this.animationMultiplier = props.animationMultiplier;
    this.translate = props.translate;
    this.width = props.width;
    this.height = props.height;
    this.isScoreHidden = props.isScoreHidden;
  }

  /**
   * Calculates the chart margins
   *
   * @returns object with top, right, bottom, and left margin in pixels
   */
  private getMargin(): Margin {
    return {
      top: 0,
      right: this.width * MARGIN_RIGHT_MAX_MULTIPLIER,
      bottom: 0,
      left: this.width * MARGIN_LEFT_MULTIPLIER,
    };
  }

  /**
   * Determines the appropriate translated string for the bar position (e.g., 'st' for 1st or 'nd' for 2nd)
   *
   * @param position: number, index of the choice + 1
   * @returns string, ordinal suffix
   */
  private getOrdinal = (position: number): string => {
    const s = [
      this.translate('ordinal_position.th'),
      this.translate('ordinal_position.st'),
      this.translate('ordinal_position.nd'),
      this.translate('ordinal_position.rd'),
    ];
    const v = position % 100;
    const suffix = (s[(v - 20) % 10] || s[v] || s[0]) ?? '';
    return position + suffix;
  };

  /**
   * Creates a vertical axis to present the ranked order
   *
   * @returns array of resolved animation promises
   */
  private updateAxis = (): Promise<void[]> => {
    const { fillColors, textColor } = this;
    // Select
    const tick = d3
      .select(this.selector)
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`)
      .selectAll<SVGGElement, Data>('.axis-tick')
      .data(this.data);

    // Enter
    const tickEnter = tick
      .enter()
      .append('g')
      .attr('class', 'axis-tick')
      .attr('opacity', 0);
    const tickText = tickEnter
      .append('text')
      .attr('x', -Y_AXIS_PADDING)
      .attr('dy', '0.35em')
      .attr('font-weight', 600)
      .attr('text-anchor', 'end');

    // Update
    const tickTransition = tick
      .merge(tickEnter)
      .transition()
      .delay(ANIMATION_DELAY * this.animationMultiplier)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr('transform', (d) => `translate(0, ${this.y(d.id)})`)
      .attr('opacity', this.isScoreHidden ? 0 : 1)
      .transition()
      .duration((ANIMATION_DURATION * this.animationMultiplier) / 2)
      .attr('transform', (d) => `translate(0, ${this.y(d.id)})`)
      .end();
    const selection = tick.select<SVGTextElement>('text').merge(tickText);
    if (
      shouldUpdateText({
        selection,
        texts: this.data.map((_, i) => this.getOrdinal(i + 1)),
        maxWidth: this.margin.left,
        maxHeight: this.y.bandwidth(),
      })
    ) {
      selection
        .text((_, i) => this.getOrdinal(i + 1)) // e.g., 1st, 2nd, 3rd...
        .call(wordWrap, {
          maxWidth: this.margin.left,
          maxHeight: this.y.bandwidth(),
          fontSize: LARGE_FONT_SIZE,
        });
    }
    const tickTextTransition = tick
      .select<SVGTextElement>('text')
      .merge(tickText)
      .transition()
      .delay(ANIMATION_DELAY * this.animationMultiplier)
      .duration((ANIMATION_DURATION * this.animationMultiplier) / 2)
      .attr('y', this.y.bandwidth() / 2)
      .attr('fill', function (d) {
        return (this as any)._id !== d.id ? (fillColors[0] ?? '') : textColor;
      })
      .attr('transform', function (d) {
        const scale = (this as any)._id !== d.id ? FONT_SIZE_MULTIPLIER : 1;
        return `scale(${scale})`;
      })
      .each(function (d) {
        (this as any)._id = d.id; // Keep track of sorted order
      })
      .transition()
      .duration((ANIMATION_DURATION * this.animationMultiplier) / 2)
      .attr('fill', this.textColor)
      .attr('transform', 'scale(1)')
      .end();

    // Exit
    tick
      .exit()
      .transition()
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr('opacity', 0)
      .remove();

    return Promise.all([tickTransition, tickTextTransition]);
  };

  /**
   * Adds bars to the visualization
   *
   * @returns array of resolved animation promises
   */
  private updateBars = (): Promise<void[]> => {
    const { x, y } = this;
    function transformBarText(this: SVGGraphicsElement, d: Data) {
      const textHeight = this.getBBox().height;
      const xPos = (max([BAR_MIN_WIDTH, x(d.score)]) ?? 0) + BAR_TEXT_PADDING;
      const yPos = y.bandwidth() / 2 + textHeight / 2;
      return `translate(${xPos}, ${yPos})`;
    }

    const easing = d3.easeBack;
    // Select
    const bar = d3
      .select(this.selector)
      .selectAll<SVGGElement, Data>('.bar-group')
      .data(this.data, (d) => d.id);

    // Enter
    const barEnter = bar
      .enter()
      .append('g')
      .attr('class', 'bar-group')
      .attr('opacity', 0);
    const barRect = barEnter
      .append('rect')
      .attr('class', 'bar-item')
      .attr('width', BAR_MIN_WIDTH)
      .attr('height', this.y.bandwidth());
    const barText = barEnter
      .append('text')
      .attr('class', 'bar-text')
      .attr('transform', transformBarText)
      .attr('dy', '-0.35em');

    // Update
    const barTransition = bar
      .merge(barEnter)
      .transition()
      .delay(ANIMATION_DELAY * this.animationMultiplier)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr('transform', (d) => `translate(0,${this.y(d.id)})`)
      .attr('opacity', 1)
      .end();
    const barRectTransition = bar
      .select<SVGRectElement>('.bar-item')
      .merge(barRect)
      .attr(
        'fill',
        (d) => this.fillColors[d.index % this.fillColors.length] ?? null,
      )
      .attr(
        'stroke',
        (d) => this.fillColors[d.index % this.fillColors.length] ?? null,
      )
      .transition()
      .ease(easing)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr('width', (d) => max([BAR_MIN_WIDTH, this.x(d.score)]) ?? null)
      .attr('height', this.y.bandwidth())
      .end();
    const selection = bar.select<SVGTextElement>('.bar-text').merge(barText);
    selection.attr('fill', this.textColor);
    if (
      shouldUpdateText({
        selection,
        texts: this.data.map((d) => d.label),
        maxWidth: this.margin.right,
        maxHeight: this.y.bandwidth(),
      })
    ) {
      selection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: this.margin.right,
          maxHeight: this.y.bandwidth(),
          fontSize: LARGE_FONT_SIZE,
          verticallyAlignMiddle: true,
        });
    }
    const barTextTransition = bar
      .select<SVGTextElement>('.bar-text')
      .merge(barText)
      .transition()
      .ease(easing)
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .attr('transform', transformBarText)
      .end();

    // Exit
    bar
      .exit()
      .transition()
      .duration(ANIMATION_DURATION * this.animationMultiplier)
      .remove();

    return Promise.all([barTransition, barRectTransition, barTextTransition]);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns array of methods that return promises (animations)
   */
  public update = (): Promise<void[][]> => {
    this.data = [...this.data].sort((a, b) => b.score - a.score);
    this.margin = this.getMargin();
    const maxScore = max(this.data.map((r) => r.score));
    const rangeOptions = rangeBandOptions(this.height, this.data.length);
    this.x
      .domain([0, max([maxScore ?? 0, 1]) ?? 0])
      .range([0, this.width - this.margin.left - this.margin.right]);
    this.y
      .domain(this.data.map((d) => d.id))
      .range(rangeOptions.r)
      .padding(rangeOptions.p);

    const axisTransition = this.updateAxis();
    const barsTransition = this.updateBars();

    return Promise.all([axisTransition, barsTransition]);
  };
}
